/**
 * The OAuth popup's other half.
 *
 * Connecting Google opens a 520x650 popup at the backend's consent endpoint. Google sends the
 * merchant back to `/api/integrations/google/callback`, which 302s to
 * `${GOOGLE_APP_RETURN_URL}?google=connected|denied|failed`, and that lands in the POPUP, which
 * until now booted the entire authenticated app inside it: contexts, analytics, the settings
 * page, the lot, in a window the size of a phone. The merchant was left looking at a broken
 * miniature of the app with no idea the connection had worked (2026-09-23).
 *
 * So the popup announces its result to whoever opened it and closes. The opener hears it and
 * stops waiting immediately, and can say what happened when the answer was "denied" or "failed"
 * rather than polling in silence until it times out.
 *
 * Nothing here assumes the message arrives: every caller keeps its poll. A popup can be closed
 * by hand, blocked, or reopened in a tab where `window.opener` is gone, and the connection is
 * real either way, the API is the truth, this is only the fast path.
 */

export const OAUTH_RESULT_MESSAGE = 'flowgate:oauth-result';

/** The query parameter each provider reports its outcome in. */
const RESULT_PARAMS = { google: 'google' };

/** What the backend can say (IntegrationController.callback). */
export const OAUTH_RESULTS = ['connected', 'denied', 'failed'];

const resultIn = (search) => {
  const params = new URLSearchParams(search);
  const found = Object.entries(RESULT_PARAMS)
    .map(([provider, key]) => [provider, params.get(key)])
    .find(([, value]) => OAUTH_RESULTS.includes(value));
  return found ? { provider: found[0], result: found[1] } : null;
};

/**
 * Hand the result to the opener and close, when this window is the OAuth popup coming back.
 *
 * Called before React renders anything, so the popup never pays for an app it is about to
 * throw away.
 *
 * @returns {{provider: string, result: string}|null} the result when this window handled it and
 *   the caller must NOT render the app; null when this is an ordinary page load.
 */
export const closeIfOAuthPopup = (win = typeof window === 'undefined' ? null : window) => {
  if (!win || !win.opener || win.opener === win) return null;
  const outcome = resultIn(win.location.search);
  if (!outcome) return null;
  try {
    win.opener.postMessage({ type: OAUTH_RESULT_MESSAGE, ...outcome }, win.location.origin);
  } catch (e) {
    // A closed or cross-origin opener is not a failure: the poll still finds the connection.
  }
  try {
    win.close();
  } catch (e) {
    // Some browsers refuse; the caller renders the "you can close this" note instead.
  }
  return outcome;
};

/**
 * Listen for a popup's result. Returns an unsubscribe function.
 *
 * Same-origin only, the popup posts to its own origin and this checks the sender, because the
 * message decides what the settings page tells the merchant.
 */
export const onOAuthResult = (handler, win = window) => {
  const listener = (event) => {
    if (event.origin !== win.location.origin) return;
    const data = event.data;
    if (!data || data.type !== OAUTH_RESULT_MESSAGE) return;
    handler({ provider: data.provider, result: data.result });
  };
  win.addEventListener('message', listener);
  return () => win.removeEventListener('message', listener);
};

/** Open the consent popup, and call `onResult` the moment it reports back. */
export const openOAuthPopup = (url, onResult, win = window) => {
  win.open(url, '_blank', 'width=520,height=650');
  const stop = onOAuthResult((outcome) => {
    stop();
    onResult(outcome);
  }, win);
  return stop;
};
